import { type IEvent, type Share } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type DeviceId } from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import axios, { type AxiosError } from 'axios';
import { CloudVmsApi, VmsApi } from 'clients';
import { type EventCamera } from 'clients/PlatformApi';
import * as CameraUtil from 'utils/CameraUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import SystemReport from 'utils/SystemReport';
import {
  CAMERA_STREAM,
  CLOUD_DOWNLOAD_MP4,
  CLOUD_DOWNLOAD_ZIP,
  CLOUD_STATUS_COMPLETE,
  CLOUD_STATUS_IN_PROGRESS,
  CLOUD_STATUS_VERIFY,
  DEFAULT_PUSH_PRIORITY,
  type PushToCloudStatus,
  VIDEO_SOURCE_CLOUD,
  VIDEO_SOURCE_LOCAL,
  VISIBILITY_PRIVATE,
  VISIBILITY_PUBLIC,
} from 'vd-constants';
import { type AccessibleAddresses, getAccessibleAddressForCamera } from '../SavePanel';
import {
  type CamerasVideoSourceMap,
  type CameraVideoSource,
  type CloudDownload,
  type EventCameraPayload,
  type ModifiedShare,
  type SavePanelCamera,
  type SavePanelCamerasById,
} from '../types';

function isAxiosTimedOut(error: AxiosError | unknown) {
  return axios.isAxiosError(error) && error.code === 'ECONNABORTED';
}

function shouldTreatAsHasVideo(results: Awaited<ReturnType<typeof fetchStreamPlaylist>>) {
  return (
    (!axios.isAxiosError(results) && results.segments.length > 0) ||
    /** If the request timeout, should treat as the camera has video as it's out best guess. */
    isAxiosTimedOut(results)
  );
}

/* The payload for existing event camera shouldn't have thumbnail as we're not
 * updating the thumbnail for existing event camera.
 */
function createExistingEventCameraPayload(camera: CameraVideoSource) {
  const { streamId, id, chId, name, streams } = camera;
  return {
    id,
    chId,
    name,
    streams: streams.map((stream) => {
      const isClipVideoStream = stream.id === streamId;
      return {
        id: stream.id,
        name: stream.name,
        ...(isClipVideoStream ? { totalSegments: INITIAL_SEGMENTS_NUMBER } : {}),
      };
    }),
  };
}

function createNewEventCamera(camera: CameraVideoSource, thumbnail: string) {
  return {
    thumbnail,
    ...createExistingEventCameraPayload(camera),
  };
}

/** When a event's camera stream has `totalSegments: -1`,
 * the backend knows this is the stream the clip video is from.
 * -1 means the video upload hasn't started. */
export const INITIAL_SEGMENTS_NUMBER = -1;

/** Check if video from a stream is still in local VMS based on what the stream claims.
 * Note that it's possible for the earliestSegmentDate to be random sometimes.
 * @param earliestSegmentDate - earliest segment date in the stream
 * @param endTime - end time of the video to be saved
 */
export function isVideoInLocal(
  earliestSegmentDate: number | string,
  endTime: number | Date | string,
) {
  const earliestSegmentTimestamp =
    typeof earliestSegmentDate === 'string' ? parseInt(earliestSegmentDate) : earliestSegmentDate;
  return earliestSegmentTimestamp < new Date(endTime).getTime();
}

/** Check if no available video for clip for the specified streams */
export function noAvailableVideoForClip(camerasVideoSourceMap: CamerasVideoSourceMap) {
  return !Object.values(camerasVideoSourceMap).some((camera) => camera.source);
}

/** Check if any camera has no video for clip for the specified streams */
export function hasNoVideoCameraForClip(camerasVideoSourceMap: CamerasVideoSourceMap) {
  return Object.values(camerasVideoSourceMap).some((camera) => !camera.source);
}

/** Check if video from the streams only have one available video quality
 *  during the specified start and end time.
 *  Other than the camera that has no video,
 *  if the remaining cameras have only one video quality.
 */
export function onlyOneVideoQuality(camerasVideoSourceMap: CamerasVideoSourceMap, endTime: Date) {
  for (const clipVideoCamera of Object.values(camerasVideoSourceMap)) {
    const { streams, source } = clipVideoCamera;
    if (source && streams.length > 1) {
      // if the other stream has video in retention, the camera has more than one video quality
      const otherStream = streams.find((stream) => stream.id !== clipVideoCamera.streamId);
      if (
        otherStream?.earliestSegmentDate &&
        isVideoInLocal(otherStream.earliestSegmentDate, endTime)
      ) {
        return false;
      }
    }
  }
  return true;
}

/** Whether the clip cameras only have SD video available */
export function onlySDVideoAvailable(camerasVideoSourceMap: CamerasVideoSourceMap, endTime: Date) {
  return (
    onlyOneVideoQuality(camerasVideoSourceMap, endTime) &&
    !Object.values(camerasVideoSourceMap).find((camera) => camera.streamName === CAMERA_STREAM.HD)
  );
}

/** Generate the cameras array for new clip event */
export function generateNewEventCameras({
  camerasVideoSourceMap,
  selectedCamerasDict,
}: {
  camerasVideoSourceMap: CamerasVideoSourceMap;
  selectedCamerasDict: SavePanelCamerasById;
}) {
  const eventCameras: EventCamera[] = [];
  for (const camera of Object.values(camerasVideoSourceMap)) {
    const { id, source } = camera;
    if (source) {
      const eventCamera = createNewEventCamera(camera, selectedCamerasDict[id].thumbnail);
      eventCameras.push(eventCamera);
    }
  }
  return eventCameras;
}

/** Generate the cameras payload when saving existing event as a clip */
export function generateNewEventCamerasPayload(camerasVideoSourceMap: CamerasVideoSourceMap) {
  const eventCamerasPayload: EventCameraPayload[] = [];
  for (const camera of Object.values(camerasVideoSourceMap)) {
    const { source } = camera;
    if (source) {
      const eventCameraPayload = createExistingEventCameraPayload(camera);
      eventCamerasPayload.push(eventCameraPayload);
    }
  }
  return eventCamerasPayload;
}

/** Check if all the clip's available video are in the cloud */
export function allAvailableVideoInCloud(camerasVideoSourceMap: CamerasVideoSourceMap) {
  return !Object.values(camerasVideoSourceMap).some(
    (camera) => camera.source && camera.source !== VIDEO_SOURCE_CLOUD,
  );
}

/** Get the cameras' info to send to Mixpanel analytics events */
export function getCameraAnalyticsInfo(camerasVideoSourceMap: CamerasVideoSourceMap) {
  const streamNames: string[] = [];
  const cameraNames: string[] = [];
  const cameraIds: string[] = [];
  const sources: string[] = [];
  Object.values(camerasVideoSourceMap).forEach((camera) => {
    const { name, id, streamName, source } = camera;
    cameraIds.push(id);
    cameraNames.push(name);
    streamNames.push(streamName);
    sources.push(source || '');
  });
  return {
    cameraIds: cameraIds.join(','),
    cameraNames: cameraNames.join(','),
    streamNames: streamNames.join(','),
    sources: sources.join(','),
  };
}

/** Determines whether the shared clip used best quality of video.
 * When adding new cameras to a shared clip,
 * we need to check the clip's existing stream data to determine which quality
 * to use for the new camera.
 * 1. If one of the clip's camera has both SD and HD streams and we used
 * the HD stream, we can be sure that the clip should use best quality streams.
 * 2. If any of the clip's camera has both SD and HD streams and we used
 * the SD stream, we can be sure that the clip should not use best quality streams.
 * 3. If all the clip's cameras have only one stream, we should use
 * the best quality streams.
 **/
export function isSharedClipBestQuality(
  cameras: {
    streams: { name: string; totalSegments?: number }[];
  }[],
) {
  for (const camera of cameras) {
    const { streams } = camera;
    const sharedStream = streams.find((stream) => stream.totalSegments != null);
    if (streams.length > 1 && sharedStream?.name === CAMERA_STREAM.SD) {
      return false;
    }
  }
  return true;
}

export function getClipDefaultVisibility() {
  const canPublic = FeaturesUtil.hasPublicSharesEnabled();
  const canPrivate = FeaturesUtil.hasPrivateSharesEnabled();

  return !canPublic && canPrivate ? VISIBILITY_PRIVATE : VISIBILITY_PUBLIC;
}

export function getClipVisibility(event: IEvent) {
  return event.scope?.visibility;
}

export function getSelfShare(shares?: Share[]): Partial<ModifiedShare> | undefined {
  if (!shares?.length) {
    return {};
  }

  return shares.find((share) => {
    const { sharedBy, email } = share;
    return sharedBy === email;
  });
}

export function isStreamMarkedSaved(stream: { id: string; totalSegments?: number }) {
  return Object.keys(stream).includes('totalSegments');
}

export function zipDownloadIsSupported(cameraName: string) {
  const sr = new SystemReport().report;
  return CameraUtil.is360({ name: cameraName }) && sr.os.name === 'Windows';
}

/** Fetch the video playlist of a certain camera for the specified duration */
export async function fetchStreamPlaylist(params: {
  accessibleAddress: string | undefined;
  cameraId: string;
  endTime: Date | number | string;
  locationId: DeviceId;
  resetAbortController: (cameraId: string) => void;
  signal?: AbortSignal;
  startTime: Date | number | string;
  streamId: string;
  useCloud?: boolean;
  user: any;
}) {
  const {
    cameraId,
    streamId,
    startTime,
    endTime,
    useCloud,
    signal,
    user,
    accessibleAddress,
    locationId,
    resetAbortController,
  } = params;
  try {
    if (!accessibleAddress) {
      throw new Error('Missing accessibleAddress');
    }
    const connect = VmsApi(accessibleAddress, false);
    const requestParams = {
      tenantId: user.customerId,
      deviceId: locationId,
      camId: cameraId,
      streamId,
      start: new Date(startTime).getTime(),
      end: new Date(endTime).getTime(),
      forceCloud: useCloud,
      merge: useCloud,
      signal,
    };

    const results = await connect.listVideos(requestParams);
    resetAbortController(cameraId);
    return results;
  } catch (error) {
    console.warn('Stream video playlist request failed', error);
    resetAbortController(cameraId);
    if (axios.isAxiosError(error)) {
      console.debug(error);
      return error;
    }
    throw error;
  }
}

/** Get info of where clip video comes from for this camera */
// TODO: maybe we should always get the local VMS playlist for both streams for the clip duration.
// This let us know how many streams are really available as the stream can lie about the earliestSegmentDate.
// This probably can simplify the logic quite a bit.
export async function generateNewCameraVideoSource({
  accessibleAddress,
  camera,
  cancelPreviousRequest,
  currentVideoCamera,
  endTime,
  isBestQuality,
  locale,
  resetAbortController,
  setNewRequestAbortController,
  startTime,
  user,
}: {
  accessibleAddress?: string;
  camera: SavePanelCamera;
  cancelPreviousRequest: (cameraId: string) => void;
  currentVideoCamera: CameraVideoSource | undefined;
  endTime: number | Date | string;
  isBestQuality: boolean;
  locale: LocaleDefinition;
  resetAbortController: (cameraId: string) => void;
  setNewRequestAbortController: (cameraId: string) => AbortSignal;
  startTime: number | Date | string;
  user: any;
}): Promise<CameraVideoSource> {
  try {
    if (new Date(startTime).valueOf() > new Date(endTime).valueOf()) {
      throw new Error('Start time is later than end time');
    }

    const streams = camera.streams;
    const streamsSortedByQuality = CameraUtil.sortStreamsByQuality({
      streams,
      desc: isBestQuality,
    });
    const firstStream = streamsSortedByQuality[0];
    const isOnlyStream = streams.length === 1;
    const streamLabelSuffix = isOnlyStream ? ` ${locale.camera.addOnStreamLabelOnlySuffix}` : '';
    const highestQualityStream = CameraUtil.sortStreamsByQuality({ streams })[0];

    /** the first prefered stream with local video that is still in retention */
    const firstLocalStream = streamsSortedByQuality.find(
      ({ earliestSegmentDate }) =>
        earliestSegmentDate && isVideoInLocal(earliestSegmentDate, endTime),
    );

    const cameraInfo = {
      id: camera.id,
      chId: camera.chId,
      name: camera.name,
      streams: camera.streams.map(
        ({ name, id, earliestSegmentDate, enableMotionlessRetention }) => ({
          name,
          id,
          earliestSegmentDate,
          enableMotionlessRetention,
        }),
      ),
    };
    const cameraWithCloudVideo = {
      ...cameraInfo,
      /** Cloud use merged best quality video, which stream doesn't really matter here.
       *  But we need to set totalSegments to one stream, so we use the HD stream or the only stream.
       *  This can guarantee that we can get the most of the merged video.
       */
      streamId: highestQualityStream.id,
      streamName: highestQualityStream.name,
      source: VIDEO_SOURCE_CLOUD,
      sourceLabel: locale.general.cloud,
    };
    const cameraWithNoVideo = {
      ...cameraInfo,
      streamId: firstStream.id,
      streamName: firstStream.name,
      streamLabel: firstStream.name + streamLabelSuffix,
      sourceLabel: locale.cloudShare.noVideo,
    };

    cancelPreviousRequest(camera.id);
    const abortControllerSignal = setNewRequestAbortController(camera.id);
    const playlistParamsBase = {
      accessibleAddress,
      cameraId: camera.id,
      endTime,
      locationId: camera.deviceId,
      resetAbortController,
      signal: abortControllerSignal,
      startTime,
      user,
    };
    const cloudPlaylistParams = {
      ...playlistParamsBase,
      streamId: firstStream.id,
      useCloud: true,
    };

    /** If all local video is out of retention, we check if cloud has video for this duration.
     * We make API requests to check if playlist is empty.
     */
    if (!firstLocalStream) {
      const results = await fetchStreamPlaylist(cloudPlaylistParams);
      if (axios.isCancel(results)) {
        /** If the request is cancelled, return the current clip video camera info as there is no need to update. */
        return currentVideoCamera || cameraWithCloudVideo;
      } else if (shouldTreatAsHasVideo(results)) {
        return cameraWithCloudVideo;
      }
      return cameraWithNoVideo;
    }

    /** Some stream are supposed to have local video in retention.
     * Check if the first local stream has video in retention.
     */
    const cameraWithLocalVideo = {
      ...cameraInfo,
      streamId: firstLocalStream.id,
      streamName: firstLocalStream.name,
      streamLabel: firstLocalStream.name + streamLabelSuffix,
      sourceLabel: locale.general.local,
      source: VIDEO_SOURCE_LOCAL,
      enableMotionlessRetention: firstLocalStream.enableMotionlessRetention,
    };
    const results = await fetchStreamPlaylist({
      ...playlistParamsBase,
      streamId: firstLocalStream.id,
    });

    if (axios.isCancel(results) || axios.isAxiosError(results)) {
      return currentVideoCamera || cameraWithLocalVideo;
    } else if (isAxiosTimedOut(results)) {
      /** If the request timeout, return the camera with local video info.
       *  When we talk to the device, the video is probably still in retention */
      return cameraWithLocalVideo;
    }

    const { segments: localVideoSegments } = results;
    /** If local video should be in retention but can't get the segments, check cloud first */
    if (!localVideoSegments?.length) {
      const results = await fetchStreamPlaylist(cloudPlaylistParams);

      if (axios.isCancel(results)) {
        return currentVideoCamera || cameraWithLocalVideo;
      } else if (shouldTreatAsHasVideo(results)) {
        return cameraWithCloudVideo;
      }
      /** The first stream has no local or cloud video.
       * Remove it from the available streams and check if the other stream has local video.
       */
      const streams = cameraInfo.streams.filter(({ id }) => id !== firstLocalStream.id);
      cameraInfo.streams = streams;

      const otherStream = streams.find((stream) => stream.id !== firstLocalStream.id);
      if (otherStream) {
        const cameraWithLocalVideoInOtherStream = {
          ...cameraInfo,
          streamId: otherStream.id,
          streamName: otherStream.name,
          streamLabel: otherStream.name + streamLabelSuffix,
          sourceLabel: locale.general.local,
          source: VIDEO_SOURCE_LOCAL,
          enableMotionlessRetention: otherStream.enableMotionlessRetention,
        };
        const results = await fetchStreamPlaylist({
          ...playlistParamsBase,
          streamId: otherStream.id,
        });
        if (axios.isCancel(results) || axios.isAxiosError(results)) {
          return currentVideoCamera || cameraWithLocalVideoInOtherStream;
        } else if (isAxiosTimedOut(results)) {
          /** If the request timeout, return the camera with local video info.
           *  When we talk to the device, the video is probably still in retention */
          return cameraWithLocalVideoInOtherStream;
        }
        const { segments: otherStreamSegments } = results;
        if (otherStreamSegments?.length) {
          return cameraWithLocalVideoInOtherStream;
        }
      }

      return cameraWithNoVideo;
    } else if (isBestQuality && firstLocalStream.name !== 'HD') {
      /** If user wants best quality video but the first stream that has local video is SD,
       *  we need to check if cloud has video for this duration.
       *  If cloud has video and the segments are not less than the local video,
       *  it has a better chance to be better quality video.
       *  In this case we use the cloud video, otherwise we use the local video.
       */
      const results = await fetchStreamPlaylist(cloudPlaylistParams);

      if (axios.isCancel(results) || axios.isAxiosError(results)) {
        return currentVideoCamera || cameraWithLocalVideo;
      } else if (results.segments?.length >= localVideoSegments.length) {
        return cameraWithCloudVideo;
      }
      /** if the request timeout, we should return the camera with local video info
       *  as cloud is unknown. it's better to give the user local video in SD as it has more chance
       *  to be more complete than cloud video */
      return cameraWithLocalVideo;
    }
    /** Use local video from the first stream. */
    return cameraWithLocalVideo;
  } catch (error) {
    throw new Error('Error getting stream to save for camera: ' + error);
  }
}

/** Get the info of where the clip video comes from for all the specified cameras */
export async function generateNewCamerasVideoSource(params: {
  accessibleAddresses: AccessibleAddresses;
  cameras: SavePanelCamerasById;
  cancelPreviousRequest: (cameraId: string) => void;
  clipCamerasVideoSourceDict: CamerasVideoSourceMap;
  endTime: number | Date | string;
  isBestQuality: boolean;
  locale: LocaleDefinition;
  user: any;
  resetAbortController: (cameraId: string) => void;
  setNewRequestAbortController: (cameraId: string) => AbortSignal;
  startTime: number | Date | string;
  updateIsCheckingVideo: (value: boolean) => void;
}) {
  const {
    cameras,
    updateIsCheckingVideo,
    clipCamerasVideoSourceDict,
    startTime,
    endTime,
    accessibleAddresses,
  } = params;
  try {
    if (new Date(startTime).valueOf() > new Date(endTime).valueOf()) {
      throw new Error('Start time is later than end time');
    }

    updateIsCheckingVideo(true);
    const camerasVideoSourceMap: CamerasVideoSourceMap = {};
    for (const cameraId of Object.keys(cameras)) {
      const camera = cameras[cameraId];
      const accessibleAddress = getAccessibleAddressForCamera(camera, accessibleAddresses);

      const clipVideoCamera = await generateNewCameraVideoSource({
        ...params,
        accessibleAddress,
        camera,
        currentVideoCamera: clipCamerasVideoSourceDict[cameraId],
      });
      camerasVideoSourceMap[cameraId] = clipVideoCamera;
    }
    return camerasVideoSourceMap;
  } catch (error) {
    throw new Error('Error getting all streams to save: ' + error);
  } finally {
    updateIsCheckingVideo(false);
  }
}

/** Get the stream that video comes from for a camera of the created clips
 *  and determine if the video is in local or cloud.
 *  The created clip can be still uploading or finish uploaded.
 */
export async function generateSavedCameraVideoSource({
  accessibleAddress,
  camera,
  cancelPreviousRequest,
  currentVideoCamera,
  endTime,
  eventCamera,
  isBestQuality,
  locale,
  locationId,
  pushToCloudStatus,
  resetAbortController,
  setNewRequestAbortController,
  shareStartTime,
  shareEndTime,
  startTime,
  user,
  faceblur,
}: {
  accessibleAddress?: string;
  camera: SavePanelCamera;
  cancelPreviousRequest: (cameraId: string) => void;
  currentVideoCamera: CameraVideoSource | undefined;
  endTime: number | Date | string;
  eventCamera: EventCamera;
  isBestQuality?: boolean;
  locale: LocaleDefinition;
  locationId: DeviceId;
  pushToCloudStatus?: PushToCloudStatus;
  resetAbortController: (cameraId: string) => void;
  setNewRequestAbortController: (cameraId: string) => AbortSignal;
  shareStartTime?: string;
  shareEndTime?: string;
  startTime: number | Date | string;
  user: any;
  faceblur: boolean;
}): Promise<CameraVideoSource> {
  try {
    if (new Date(startTime).valueOf() > new Date(endTime).valueOf()) {
      throw new Error('Start time is later than end time');
    }

    const { streams, chId, id: cameraId } = camera;
    const savedStream = eventCamera.streams.find((stream) => isStreamMarkedSaved(stream));
    const isOnlyStream = streams.length === 1;
    const streamLabelSuffix = isOnlyStream ? ` ${locale.camera.addOnStreamLabelOnlySuffix}` : '';

    if (savedStream) {
      const { id: streamId, name } = savedStream;
      const callhomeCameraStream = streams.find((stream) => stream.id === streamId);
      /** The event camera stream has earliestSegmentDate as well but that value is not accurate.
       *  So we have to use the callhome camera stream to get the earliestSegmentDate.
       */
      const earliestSegmentDate = callhomeCameraStream?.earliestSegmentDate;
      const clipVideoCameraBase = {
        id: cameraId,
        chId,
        name: camera.name,
        streamId,
        streamName: name,
        streams: streams.map(({ name, id, enableMotionlessRetention }) => ({
          name,
          id,
          enableMotionlessRetention,
        })),
      };

      /** If the clip's upload is complete
       * and the totalSegments of the stream is not -1 or 0,
       * and the start and end time is the original shared start and end time,
       * we should assume the video is uploaded successfully and in cloud.
       */
      const isPeriodSameAsOriginalSharedPeriod =
        shareStartTime &&
        shareEndTime &&
        new Date(startTime).valueOf() === new Date(shareStartTime).valueOf() &&
        new Date(endTime).valueOf() === new Date(shareEndTime).valueOf();
      const hasTotalSegments = savedStream.totalSegments && savedStream.totalSegments > 0;
      if (
        (pushToCloudStatus === CLOUD_STATUS_COMPLETE ||
          pushToCloudStatus === CLOUD_STATUS_VERIFY) &&
        hasTotalSegments &&
        isPeriodSameAsOriginalSharedPeriod &&
        !faceblur
      ) {
        return {
          ...clipVideoCameraBase,
          source: VIDEO_SOURCE_CLOUD,
          sourceLabel: locale.general.cloud,
        };
      }

      const isInLocal = earliestSegmentDate && isVideoInLocal(earliestSegmentDate, endTime);
      if (isInLocal) {
        return {
          ...clipVideoCameraBase,
          streamLabel: name + streamLabelSuffix,
          source: VIDEO_SOURCE_LOCAL,
          sourceLabel: locale.general.local,
        };
      }
      if (new Date(startTime).valueOf() > new Date(endTime).valueOf()) {
        throw new Error('Start time is later than end time');
      }
      /** If can't find the local video, we check if the cloud has video for this duration. */
      const cloudPlaylistParams = {
        accessibleAddress,
        cameraId,
        streamId,
        startTime,
        endTime,
        useCloud: true,
        user,
        locationId,
        resetAbortController,
      };
      const results = await fetchStreamPlaylist(cloudPlaylistParams);
      const cameraWithCloudVideo = {
        ...clipVideoCameraBase,
        source: VIDEO_SOURCE_CLOUD,
        sourceLabel: locale.general.cloud,
      };

      if (axios.isCancel(results)) {
        return currentVideoCamera || cameraWithCloudVideo;
      } else if (shouldTreatAsHasVideo(results)) {
        return cameraWithCloudVideo;
      }
      return {
        ...clipVideoCameraBase,
        streamLabel: name + streamLabelSuffix,
        sourceLabel: locale.cloudShare.noVideo,
      };
    }
    console.error(
      'Cannot recognize from which stream the video was uploaded for camera: ' + camera.id,
    );
    /** if the clip has been saved but no stream was marked as saved, mark the new available stream*/
    return generateNewCameraVideoSource({
      accessibleAddress,
      camera,
      cancelPreviousRequest,
      currentVideoCamera,
      endTime,
      isBestQuality: isBestQuality ?? false,
      locale,
      resetAbortController,
      setNewRequestAbortController,
      startTime,
      user,
    });
  } catch (error) {
    throw new Error(`Error getting saved stream for camera ${camera.id}: ${error}`);
  }
}

/** Get which streams were saved for all the specified cameras */
export async function generateSavedCamerasVideoSource({
  accessibleAddresses,
  cameras,
  cancelPreviousRequest,
  clipCamerasVideoSourceDict,
  endTime,
  eventCamerasDict,
  isBestQuality,
  locale,
  pushToCloudStatus,
  resetAbortController,
  setNewRequestAbortController,
  shareStartTime,
  shareEndTime,
  startTime,
  updateIsCheckingVideo,
  user,
  faceblur,
}: {
  accessibleAddresses: AccessibleAddresses;
  cameras: SavePanelCamerasById;
  cancelPreviousRequest: (cameraId: string) => void;
  clipCamerasVideoSourceDict: CamerasVideoSourceMap;
  endTime: number | Date | string;
  eventCamerasDict: { [key: string]: EventCamera };
  isBestQuality?: boolean;
  locale: LocaleDefinition;
  pushToCloudStatus?: PushToCloudStatus;
  resetAbortController: (cameraId: string) => void;
  setNewRequestAbortController: (cameraId: string) => AbortSignal;
  shareStartTime?: string;
  shareEndTime?: string;
  startTime: number | Date | string;
  updateIsCheckingVideo: (value: boolean) => void;
  user: any;
  faceblur: boolean;
}) {
  try {
    if (new Date(startTime).valueOf() > new Date(endTime).valueOf()) {
      throw new Error('Start time is later than end time');
    }

    updateIsCheckingVideo(true);
    const camerasVideoSourceMap: CamerasVideoSourceMap = {};
    for (const cameraId of Object.keys(cameras)) {
      const eventCamera = eventCamerasDict[cameraId];
      const camera = cameras[cameraId];
      const accessibleAddress = getAccessibleAddressForCamera(camera, accessibleAddresses);

      const sharedParams = {
        accessibleAddress,
        camera,
        cancelPreviousRequest,
        currentVideoCamera: clipCamerasVideoSourceDict[cameraId],
        endTime,
        locale,
        locationId: cameras[cameraId].deviceId,
        resetAbortController,
        setNewRequestAbortController,
        startTime,
        user,
      };
      const clipVideoCamera = eventCamera
        ? await generateSavedCameraVideoSource({
            ...sharedParams,
            eventCamera,
            pushToCloudStatus,
            shareEndTime,
            shareStartTime,
            faceblur,
          })
        : await generateNewCameraVideoSource({
            ...sharedParams,
            isBestQuality: isBestQuality ?? false,
          });
      camerasVideoSourceMap[cameraId] = clipVideoCamera;
    }
    return camerasVideoSourceMap;
  } catch (error) {
    throw new Error('Error getting all saved streams: ' + error);
  } finally {
    updateIsCheckingVideo(false);
  }
}

/** Generate the downloads property for new download event */
export function generateNewEventDownloads(camerasVideoSourceMap: CamerasVideoSourceMap) {
  const downloads: {
    streamId: string;
    expiration?: number;
    downloaded: { email: string; downloadedAt: number[] }[];
  }[] = [];
  Object.values(camerasVideoSourceMap).forEach(({ streamId }) => {
    if (streamId) {
      downloads.push({
        streamId,
        downloaded: [],
      });
    }
  });
  return downloads;
}

/**
 * Download can be seen ready when the mp4 file is ready to download.
 * If the camera supports zip download, we should also check if the zip file is ready.
 * If zip is supported, we can safely assume that zip is ready when mp4 is ready.
 * Both should complete at the same time, but there may still be some time to build the ZIP file.
 * There are also scenarios where zip can't be created if the video is too long (e.g. > 4 hours).
 * So we should poll the zip file after mp4 is ready for supported cameras.
 * If the mp4 request fails, we can assume that the zip file will not be created.
 */
export async function checkDownloadReady({
  cameraId,
  cameraName,
  eventId,
  streamId,
  tenantId,
}: {
  cameraId: string;
  cameraName: string;
  eventId: string;
  streamId: string;
  tenantId: string;
}) {
  const cloudDownload: CloudDownload = {
    cameraId,
    cameraName,
    streamId,
    state: CLOUD_STATUS_IN_PROGRESS,
  };
  try {
    const mp4Result = await CloudVmsApi().checkDownloadUrl({
      cameraId,
      eventId,
      streamId,
      tenantId,
      type: CLOUD_DOWNLOAD_MP4,
    });
    const isZipSupported = zipDownloadIsSupported(cameraName);

    if (mp4Result.status === 200) {
      cloudDownload.mp4Url = CloudVmsApi().getMp4DownloadUrl({
        tenantId,
        eventId,
        cameraId,
        streamId,
      });
      cloudDownload.state = CLOUD_STATUS_COMPLETE;

      if (isZipSupported) {
        const zipResult = await CloudVmsApi().checkDownloadUrl({
          cameraId,
          eventId,
          streamId,
          tenantId,
          type: CLOUD_DOWNLOAD_ZIP,
        });
        if (zipResult.status === 200) {
          cloudDownload.zipUrl = CloudVmsApi().getZipDownloadUrl({
            tenantId,
            eventId,
            cameraId,
            streamId,
          });
        }
      }
    }
  } catch (error) {
    console.debug('Checking if download is ready for camera ' + cameraName, error);
  }
  return cloudDownload;
}

/** Request VMS to push local video to cloud for clip, for one camera */
export function pushLocalVideo({
  start,
  end,
  camera,
  event,
  fillGaps,
  accessibleAddress,
  priority = DEFAULT_PUSH_PRIORITY,
  saveMotionOnlyVideo,
  isExistingShare,
  faceblur,
}: {
  // 'id' here is the VMS camera ID (shorter than Callhome Camera ID and not unique)
  camera: Pick<CameraVideoSource, 'id' | 'streams' | 'streamId' | 'streamName'>;
  start: number;
  end: number;
  event: IEvent;
  fillGaps: boolean;
  accessibleAddress: string;
  priority?: number;
  saveMotionOnlyVideo: boolean;
  isExistingShare: boolean;
  faceblur: boolean;
}) {
  const { id: camId, streamName, streamId, streams } = camera;

  const targetStreamHasMotionlessRetention = streams.some(
    (stream) => stream.id === streamId && stream.enableMotionlessRetention,
  );

  /*
   * ONLY merge playlists if the following 5 conditions are met
   * 1. They have NOT checked the saveMotionOnlyVideo checkbox OR the target stream does NOT have motionless retention enabled
   * 2. The target stream is HD
   * 3. There is more than one stream
   * 4. It's not an existing share
   * 5. User didn't specify to fill the gaps
   * Currently we have no way of knowing if the original share was a merged playlist.
   * This is because nothing is saved on the event.
   * Merge=false will ensure the original share is not changed.
   */
  const merge =
    (!saveMotionOnlyVideo || !targetStreamHasMotionlessRetention) &&
    streamName === CAMERA_STREAM.HD &&
    streams.length > 1 &&
    !isExistingShare &&
    !fillGaps;

  const payload = {
    camId,
    end,
    eventId: event.id,
    start,
    streamId,
    metadata: { id: event.id, fillGaps, faceblur },
    merge,
    eventType: event.type,
  };
  return VmsApi(accessibleAddress, false).shareVideo({
    ...payload,
    priority,
  });
}

/**
 * Get the video pushing status from CloudVMS for one camera stream.
 * @param {string} cameraId - The VMS Camera ID (shorter and not unique)
 */
export async function getPushToCloudStatus({
  tenantId,
  eventId,
  deviceId,
  cameraId,
  streamId,
  start,
  end,
}: {
  tenantId: string;
  eventId: string;
  deviceId: string;
  cameraId: string;
  streamId: string;
  start: number;
  end: number;
}) {
  const payload = { eventId, deviceId, cameraId, streamId, start, end };
  try {
    const results = await CloudVmsApi().getPushToCloudStatus(tenantId, payload);
    return results;
  } catch (error) {
    console.error('Error getting push to cloud status for camera ' + cameraId, error);
    throw error;
  }
}
